import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Empty,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { usePagination } from '../shared/usePagination';
import { QP, useQueryParams } from '../shared/useQueryParams';
import { useAuthStore } from '../utils/stores/useAuthStore';
import {
  ImportMenuPriceReport,
  MenuPriceRow,
  useExportMenuPrices,
  useMenuPriceLocations,
  useMenuPrices,
  useUpdateMenuPrice,
  useCreateMenuProduct,
} from '../utils/api-hooks/menu-price.hook';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const menuPricesQuerySchema = {
  range: QP.dayjsRange('startDate', 'endDate'),
  locationIds: QP.stringArray('locationIds'),
  providerName: QP.string('providerName'),
  productName: QP.string('productName'),
  missingPriceOnly: QP.boolean('missingPriceOnly'),
};

const COMMERCIAL_TERMS_LABEL: Record<string, string> = {
  BUYOUT: '買斷',
  COMMISSION: '抽成',
  GAMBLING: '賭約',
};

/** 本週一 ~ 下週日。dayjs 的 startOf('week') 依 locale 可能是週日，這裡自己算週一 */
function getDefaultRange() {
  const today = dayjs().startOf('day');
  const dayOfWeek = today.day(); // 0=Sun, 1=Mon ...
  const monday = today.add(dayOfWeek === 0 ? -6 : 1 - dayOfWeek, 'day');

  return { startTime: monday, endTime: monday.add(13, 'day') };
}

export function MenuPricesPage() {
  const pagination = usePagination({ defaultPageSize: 50, syncUrl: true });
  const { values, setValues } = useQueryParams(menuPricesQuerySchema);
  const { productName, providerName, range, missingPriceOnly } = values;
  const queryClient = useQueryClient();
  const token = useAuthStore((state) => state.token);

  const locationIds = useMemo(
    () => values.locationIds?.map(Number) ?? [],
    [values.locationIds],
  );

  // 預設帶入本週一 ~ 下週日。
  // 刻意不用「今天 +14 天」：易碩通常只排到一週多一點，那樣的範圍有一半
  // 落在還沒排菜單的日子，一進頁面就是大片空白。
  useEffect(() => {
    if (!range.startTime && !range.endTime) {
      setValues({ range: getDefaultRange() });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: locations = [] } = useMenuPriceLocations();
  const { data, isFetching } = useMenuPrices({
    skip: pagination.skip,
    take: pagination.take,
    startDate: range.startTime,
    endDate: range.endTime,
    locationIds: locationIds.length ? locationIds : undefined,
    productName: productName || undefined,
    providerName: providerName || undefined,
    missingPriceOnly,
  });
  const { mutateAsync: update, isLoading: isUpdating } = useUpdateMenuPrice();
  const { mutate: exportXlsx, isLoading: isExporting } = useExportMenuPrices();
  const { mutateAsync: createProduct, isLoading: isCreating } =
    useCreateMenuProduct();

  const columns: ColumnsType<MenuPriceRow> = useMemo(
    () => [
      {
        title: '供餐日期',
        dataIndex: 'firstDate',
        width: 180,
        render: (_v, row) =>
          row.firstDate === row.lastDate
            ? row.firstDate
            : `${row.firstDate} ~ ${row.lastDate} (${row.dateCount} 天)`,
      },
      {
        title: '大樓',
        dataIndex: 'locations',
        width: 160,
        render: (locs: MenuPriceRow['locations']) => (
          <Space wrap size={[4, 4]}>
            {locs.map((l) => (
              <Tag key={l.id} style={{ marginInlineEnd: 0 }}>
                {l.name || `#${l.id}`}
              </Tag>
            ))}
          </Space>
        ),
      },
      {
        title: '餐廳',
        dataIndex: 'esitProviderName',
        render: (v: string | null) => v || '-',
      },
      {
        title: '商品',
        dataIndex: 'esitProductName',
        render: (v: string, row) => (
          <>
            <div>{v}</div>
            {row.esitProductEnName && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {row.esitProductEnName}
              </Text>
            )}
          </>
        ),
      },
      {
        title: '商品編號',
        dataIndex: 'idNumber',
        width: 120,
        render: (v: string | null) =>
          v || <Text type="danger">易碩未設定編號</Text>,
      },
      {
        title: '商業條件',
        dataIndex: 'commercialTerms',
        width: 90,
        align: 'center' as const,
        render: (v: string | null) =>
          v ? <Tag>{COMMERCIAL_TERMS_LABEL[v] ?? v}</Tag> : '-',
      },
      {
        title: '買斷價',
        dataIndex: 'cost',
        width: 90,
        align: 'right' as const,
        render: (v: number | null) => (v === null ? '-' : v.toLocaleString()),
      },
      {
        title: '店內價',
        dataIndex: 'inStorePrice',
        width: 230,
        render: (_v, row) => (
          <PriceEditor
            row={row}
            disabled={isUpdating}
            creating={isCreating}
            onCreateProduct={() => createProduct(row.productId)}
            onSave={(inStorePrice) =>
              update({ idNumber: row.idNumber as string, inStorePrice })
            }
          />
        ),
      },
      {
        title: '狀態',
        dataIndex: 'state',
        width: 90,
        align: 'center' as const,
        render: (v: string | null) =>
          v === null ? (
            '-'
          ) : v === 'SUPPLYING' ? (
            <Tag color="green">供應中</Tag>
          ) : (
            <Tag>停售</Tag>
          ),
      },
    ],
    [update, isUpdating, createProduct, isCreating],
  );

  const missingCount = useMemo(
    () =>
      (data?.items ?? []).filter((i) => !i.productExists || i.inStorePrice === null)
        .length,
    [data],
  );

  return (
    <Space direction="vertical" className="w-full">
      <Title level={3}>Amazon 菜單價格</Title>
      <Alert
        type="info"
        showIcon
        message="這裡列出易碩排定在該期間的所有上架商品（不論是否被訂購過）。店內價會以純金額（例如 NT$160，不加標籤）顯示在客人的訂餐頁與評價畫面，同時也是抽成／賭約供應商的對帳計價基礎。"
        description="改價後前台約 5–10 分鐘內生效（外部服務有快取）。"
      />
      <Table
        size="small"
        rowKey="productId"
        loading={isFetching}
        columns={columns}
        dataSource={data?.items ?? []}
        pagination={{ ...pagination, total: data?.total ?? 0 }}
        rowClassName={(row) =>
          !row.productExists || row.inStorePrice === null ? 'bg-red-50' : ''
        }
        scroll={{ x: 1400 }}
        locale={{
          emptyText: isFetching ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="查詢中..." />
          ) : (
            <EmptyMenu
              menuDateRange={data?.menuDateRange}
              onResetRange={() => setValues({ range: getDefaultRange() })}
            />
          ),
        }}
        title={() => (
          <Space direction="vertical" className="w-full" size="middle">
            <Space wrap size="middle">
              <RangePicker
                value={[range.startTime ?? null, range.endTime ?? null] as any}
                onChange={(v: any) =>
                  setValues({
                    range: {
                      startTime: v?.[0] ?? undefined,
                      endTime: v?.[1] ?? undefined,
                    },
                  })
                }
                format="YYYY-MM-DD"
                placeholder={['供餐日期起', '供餐日期訖']}
                allowEmpty={[false, false]}
              />
              <Select
                mode="multiple"
                allowClear
                placeholder="大樓"
                className="min-w-[200px]"
                value={locationIds}
                onChange={(v: number[]) =>
                  setValues({ locationIds: v.map(String) })
                }
                options={locations.map((l) => ({
                  value: l.location_id,
                  label: `${l.location_name} (#${l.location_id})`,
                }))}
              />
              <Input.Search
                key={`provider-${providerName}`}
                allowClear
                placeholder="餐廳名稱"
                defaultValue={providerName}
                onSearch={(v) => setValues({ providerName: v })}
                className="min-w-[180px]"
              />
              <Input.Search
                key={`product-${productName}`}
                allowClear
                placeholder="商品名稱 / 編號"
                defaultValue={productName}
                onSearch={(v) => setValues({ productName: v })}
                className="min-w-[220px]"
              />
              <Checkbox
                checked={!!missingPriceOnly}
                onChange={(e) =>
                  setValues({ missingPriceOnly: e.target.checked || undefined })
                }
              >
                只顯示缺價
              </Checkbox>
            </Space>
            <Space wrap size="middle">
              <Button
                loading={isExporting}
                onClick={() =>
                  exportXlsx({
                    startDate: range.startTime,
                    endDate: range.endTime,
                    locationIds: locationIds.length ? locationIds : undefined,
                    productName: productName || undefined,
                    providerName: providerName || undefined,
                    missingPriceOnly,
                  })
                }
              >
                匯出 Excel
              </Button>
              <ImportButton
                token={token}
                onDone={() =>
                  queryClient.invalidateQueries(['external-menu-prices'])
                }
              />
              {!isFetching && (
                <Text type={missingCount > 0 ? 'danger' : 'secondary'}>
                  本頁缺價 {missingCount} 筆 / 共 {data?.total ?? 0} 項商品
                </Text>
              )}
            </Space>
          </Space>
        )}
      />
    </Space>
  );
}

/**
 * 空結果的說明。三種完全不同的原因長得一模一樣（易碩還沒排菜單、該大樓沒供餐、
 * 篩選太窄），不講清楚的話操作人員只會覺得功能壞了。
 */
function EmptyMenu({
  menuDateRange,
  onResetRange,
}: {
  menuDateRange?: { first: string; last: string } | null;
  onResetRange: () => void;
}) {
  return (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={
        <Space direction="vertical" size={4}>
          <Text>這個日期區間沒有菜單資料</Text>
          {menuDateRange ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              易碩目前排定的菜單到 <Text strong>{menuDateRange.last}</Text> 為止
              （最早 {menuDateRange.first}）。請把日期調整到這個範圍內。
            </Text>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              易碩目前沒有排定任何未來的菜單；若已篩選大樓，也可能是該大樓這段期間沒有供餐。
            </Text>
          )}
          <Button type="link" size="small" className="px-0" onClick={onResetRange}>
            回到預設範圍（本週 ~ 下週）
          </Button>
        </Space>
      }
    />
  );
}

function PriceEditor({
  row,
  onSave,
  onCreateProduct,
  disabled,
  creating,
}: {
  row: MenuPriceRow;
  onSave: (inStorePrice: number) => Promise<unknown>;
  onCreateProduct: () => Promise<unknown>;
  disabled: boolean;
  creating: boolean;
}) {
  const [value, setValue] = useState<number | null>(row.inStorePrice);

  useEffect(() => {
    setValue(row.inStorePrice);
  }, [row.inStorePrice]);

  // 易碩沒給商品編號，連對帳都對不上，比缺價更根本
  if (!row.idNumber) {
    return <Text type="danger">無商品編號，無法設定</Text>;
  }

  // simply-carbs 還沒建這個商品。現行機制要等訂單匯入才會自動建，
  // 那時客人早就看不到價格了，所以這裡讓操作人員直接補建。
  if (!row.productExists) {
    return (
      <Space size={4}>
        <Tag color="error" style={{ marginInlineEnd: 0 }}>
          尚未建立商品
        </Tag>
        <Button
          type="link"
          size="small"
          className="px-0"
          loading={creating}
          onClick={async () => {
            await onCreateProduct();
          }}
        >
          建立商品
        </Button>
      </Space>
    );
  }

  const dirty = value !== null && value !== row.inStorePrice;

  return (
    <Space size={4}>
      <InputNumber
        min={0}
        precision={0}
        value={value}
        placeholder="未設定"
        status={row.inStorePrice === null ? 'error' : undefined}
        onChange={(v) => setValue(typeof v === 'number' ? v : null)}
        disabled={disabled}
        style={{ width: 100 }}
      />
      {row.inStorePrice === null &&
        (row.blocksStatement ? (
          <Text type="danger" strong style={{ fontSize: 12 }}>
            會擋對帳
          </Text>
        ) : (
          <Text style={{ color: '#d46b08', fontSize: 12 }}>未設定</Text>
        ))}
      {dirty && (
        <Button
          type="link"
          size="small"
          className="px-0"
          onClick={async () => {
            await onSave(value as number);
          }}
        >
          儲存
        </Button>
      )}
    </Space>
  );
}

function ImportButton({
  token,
  onDone,
}: {
  token: string | null;
  onDone: () => void;
}) {
  const [report, setReport] = useState<ImportMenuPriceReport | null>(null);

  return (
    <>
      <Upload
        accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        action={`${process.env.REACT_APP_API_SERVER_URL}/external-menu-prices/import`}
        headers={{ Authorization: `Bearer ${token}` }}
        name="file"
        showUploadList={false}
        onChange={({ file }) => {
          if (file.status === 'done') {
            setReport(file.response as ImportMenuPriceReport);
            onDone();
          }
          if (file.status === 'error') {
            message.error(file.response?.message ?? '匯入失敗');
          }
        }}
      >
        <Button type="primary">匯入 Excel</Button>
      </Upload>
      <Modal
        open={!!report}
        title="匯入結果"
        onCancel={() => setReport(null)}
        onOk={() => setReport(null)}
        cancelButtonProps={{ style: { display: 'none' } }}
        width={640}
      >
        {report && (
          <Space direction="vertical" className="w-full">
            <Text>
              讀取 {report.total} 列，更新 <Text strong>{report.updated}</Text> 筆，
              略過 {report.skipped} 筆（新店內價留白），
              {report.unchanged} 筆與現況相同。
            </Text>
            {report.notFound.length > 0 && (
              <Alert
                type="warning"
                showIcon
                message={`以下 ${report.notFound.length} 個商品編號在 simply-carbs 尚未建立，未寫入`}
                description={report.notFound.join('、')}
              />
            )}
            {report.invalid.length > 0 && (
              <Alert
                type="error"
                showIcon
                message={`以下 ${report.invalid.length} 列格式有誤，未寫入`}
                description={
                  <ul className="pl-4">
                    {report.invalid.map((i) => (
                      <li key={`${i.row}-${i.idNumber}`}>
                        第 {i.row} 列（{i.idNumber || '無編號'}）：{i.reason}
                      </li>
                    ))}
                  </ul>
                }
              />
            )}
          </Space>
        )}
      </Modal>
    </>
  );
}
